/**
 * Shared, render-agnostic packing-slip line model.
 *
 * Both standard A4 and Cheetah pagination consume this model so quantities,
 * variations, weights and prices cannot drift between layouts.
 */

export function getPackingSlipItems(order) {
  if (Array.isArray(order?.originalItems) && order.originalItems.length) return order.originalItems;
  if (Array.isArray(order?.order_items) && order.order_items.length) return order.order_items;
  if (Array.isArray(order?.items) && order.items.length) return order.items;
  return [];
}

export function getPackingSlipItemSku(item) {
  const raw =
    item?.sku ??
    item?.code ??
    item?.Code ??
    item?.product_code ??
    item?.productCode ??
    item?.product_sku ??
    item?.productSku ??
    '';
  return String(raw || '').trim().toUpperCase();
}

export function getPackingSlipItemUnitPrice(item) {
  const raw =
    item?.price ??
    item?.unit_price ??
    item?.unitPrice ??
    item?.price_per_unit ??
    item?.Price ??
    null;
  if (raw == null || raw === '') return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

export function packingSlipLineNeedsKg(name) {
  const text = String(name || '').toLowerCase();
  return text.includes('whole') || text.includes('unsliced');
}

function itemFoodNote(item) {
  const lot = String(
    item?.lot ||
      item?.lotNumber ||
      item?.lot_number ||
      item?.batch ||
      item?.batch_number ||
      item?.batchNumber ||
      item?.metadata?.lot ||
      '',
  ).trim();
  const useBy = String(
    item?.use_by ||
      item?.useBy ||
      item?.best_before ||
      item?.bestBefore ||
      item?.expiry ||
      item?.expiry_date ||
      '',
  ).trim();
  const allergen = String(item?.allergen || item?.allergens || item?.metadata?.allergen || '').trim();
  return [lot && `Lot ${lot}`, useBy && `Use by ${useBy}`, allergen && allergen]
    .filter(Boolean)
    .join(' · ');
}

function normalizeVariations(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => {
      if (Array.isArray(entry)) {
        return { key: String(entry[0] ?? ''), value: String(entry[1] ?? '') };
      }
      return { key: '', value: String(entry ?? '') };
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([key, entry]) => ({
      key: String(key),
      value: String(entry ?? ''),
    }));
  }
  return [];
}

/**
 * @param {object} order
 * @param {object} [options]
 * @param {object} [options.weightsData]
 * @param {boolean} [options.showPrices]
 * @param {boolean} [options.showKgLines]
 * @param {(sku: string, fallbackName: string) => string} [options.formatLineName]
 */
export function buildPackingSlipLineModel(order, options = {}) {
  const items = getPackingSlipItems(order);
  const orderKey = order?.orderId || order?.source_id || order?.sourceId || order?.id;
  const shippingMethod = String(
    order?.shipping_method ||
      order?.shippingMethod ||
      order?.delivery_method ||
      order?._accountDelivery?.shippingMethod ||
      '',
  ).trim();
  const metadata = {
    orderNumber: String(order?.source_id || order?.sourceId || order?.orderId || order?.id || ''),
    shippingMethod,
    email: String(order?.email || order?.customerEmail || order?.customer?.email || '').trim(),
    phone: String(
      order?.shipping?.phone || order?.customerPhone || order?.customer?.phone || '',
    ).trim(),
  };
  const orderWeights =
    (options.weightsData && orderKey && options.weightsData[orderKey]) || {};
  const showPrices = options.showPrices === true;
  const showKgLines = options.showKgLines !== false;
  const formatLineName =
    typeof options.formatLineName === 'function'
      ? options.formatLineName
      : (_sku, fallbackName) => fallbackName;

  let quantityTotal = 0;
  let moneyTotal = null;

  const lines = items.map((item, index) => {
    const quantity = Number(item?.quantity) || 0;
    const sku = getPackingSlipItemSku(item);
    const name = String(formatLineName(sku, item?.name || '') || item?.name || '').trim();
    const unitPrice = showPrices ? getPackingSlipItemUnitPrice(item) : null;
    const lineTotal = unitPrice == null ? null : unitPrice * quantity;
    const needsKg = showKgLines && packingSlipLineNeedsKg(name);
    const recordedWeight = needsKg ? Number.parseFloat(orderWeights[sku]) : null;
    const weightKg =
      Number.isFinite(recordedWeight) && recordedWeight > 0 ? recordedWeight : null;

    quantityTotal += quantity;
    if (lineTotal != null) moneyTotal = (moneyTotal || 0) + lineTotal;

    return {
      index,
      source: item,
      sku,
      name,
      quantity,
      variations: normalizeVariations(item?.variation_list),
      needsKg,
      weightKg,
      foodNote: itemFoodNote(item),
      unitPrice,
      lineTotal,
    };
  });

  const fromOrder = order?.quantity_total ?? order?.quantityTotal;
  if (fromOrder != null && fromOrder !== '' && Number.isFinite(Number(fromOrder))) {
    quantityTotal = Number(fromOrder);
  }

  return {
    order,
    orderKey,
    metadata,
    lines,
    quantityTotal,
    moneyTotal,
    showPrices,
    showKgLines,
    signature: {
      enabled: options.showSignatureLines !== false,
      receiverRequired: shippingMethod === 'Bermondsey' || shippingMethod === 'Cargo Bike',
    },
  };
}

export default buildPackingSlipLineModel;
